import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { RiBookmarkFill } from 'react-icons/ri'
import type { IBuiltInInstrumentSummary } from '@brushfire/types'
import metaDurationIcon from '@/features/instruments/templates/assets/icons/meta-duration.svg'
import metaQuestionsIcon from '@/features/instruments/templates/assets/icons/meta-questions.svg'
import { formatTemplateEstCompletionLabel } from '@/features/instruments/templates/lib/formatTemplateEstCompletion'
import { MetaUsageIcon } from '@/features/instruments/templates/components/MetaUsageIcon'
import { TemplateIcon } from '@/features/instruments/templates/components/TemplateIcon'
import { libraryBookmarkIconButtonSx, libraryCardHoverSx } from '@/shared/lib/libraryCardHover'

const CARD_BORDER = '#e2e8f0'
const TITLE = '#04183f'
const META_QUESTIONS = '#008cd0'
const META_DURATION = '#0d9467'
const META_USAGE = '#92400e'

type Props = Readonly<{
  entry: IBuiltInInstrumentSummary
  canRemove: boolean
  onOpen: () => void
  onRequestRemove: () => void
}>

export function BuiltInInstrumentCard({ entry, canRemove, onOpen, onRequestRemove }: Props) {
  return (
    <Box
      component="article"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      data-testid={`built-in-instrument-card-${entry.id}`}
      sx={{
        position: 'relative',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '12px',
        bgcolor: '#ffffff',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '24px',
        minHeight: '117px',
        p: '25px',
        textAlign: 'left',
        width: '100%',
        cursor: 'pointer',
        ...libraryCardHoverSx,
      }}
    >
      {canRemove ? (
        <IconButton
          size="small"
          aria-label="Remove from Library"
          data-testid={`built-in-instrument-remove-${entry.id}`}
          onClick={(e) => {
            e.stopPropagation()
            onRequestRemove()
          }}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            p: 0.5,
            ...libraryBookmarkIconButtonSx,
          }}
        >
          <RiBookmarkFill size={18} aria-hidden />
        </IconButton>
      ) : null}

      <Box
        sx={{
          bgcolor: '#f3e8ff',
          borderRadius: '12px',
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <TemplateIcon iconKey="brand" size={40} />
      </Box>

      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, pr: canRemove ? 3 : 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '28px',
            color: TITLE,
          }}
        >
          {entry.name}
        </Typography>

        <Stack direction="row" flexWrap="wrap" alignItems="center" gap="16px" sx={{ pt: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box component="img" src={metaQuestionsIcon} alt="" sx={{ width: 12, height: 12 }} />
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: META_QUESTIONS }}>
              {entry.questionCount} questions
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box component="img" src={metaDurationIcon} alt="" sx={{ width: 12, height: 12 }} />
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: META_DURATION }}>
              {formatTemplateEstCompletionLabel(entry.estCompletionSecs)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <MetaUsageIcon />
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: META_USAGE }}>
              Used {entry.usageCount} times
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  )
}
